import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
// Import Shared Components
import { Input } from 'shared-components';
// Import Global Components
import InfoTooltip from '../../../../../components/InfoTooltip';
import InputGroup from '../../../../../layouts/InputGroup';
import { errorMessage } from '../../../../../utils/validationHandlers';
// Import Utils
import {
  digitsOnly,
  maxCardDigits,
  expectedCvvLength,
  formatCardNumber,
  formatShortExpiry,
} from '../../../../../utils/cardFormatting';
// Import Component Styles
import { FieldError, FormRow, PasswordFieldWrapper } from '../../../styles';
import PwdEyeButton from '../PwdEyeButton';

/* eslint-disable react/prop-types */
const AuthField = ({ id, label, tooltip, children }) => (
  <InputGroup label={label} labelFor={id} page="auth" tooltip={tooltip}>
    {children}
  </InputGroup>
);

const AuthError = ({ id, children }) => (
  <FieldError id={id} className="invalid-feedback">
    {children}
  </FieldError>
);
/* eslint-enable react/prop-types */

// The pieces the fields are drawn with. Registration and activation use the portal's
// InputGroup; a page with its own look (RBC onboarding) passes its own, so every card entry
// screen keeps the same behaviour whatever it looks like. Hint is optional: when given, the CVV
// shows an inline hint (cvvHint) instead of the tooltip.
export const authLayout = {
  Field: AuthField,
  Row: FormRow,
  Input,
  PasswordWrapper: PasswordFieldWrapper,
  Error: AuthError,
  Hint: null,
};

const defaultIds = { cardNumber: 'card-number', cvv: 'cvv', expiry: 'expiry' };

// Card number, CVV and expiry date fields for the card entry screens. The fields start
// empty, and the card number and expiry are entered as printed on the card: the number
// grouped in fours, the expiry as MM/YY. Callers strip the grouping spaces (digitsOnly) and
// expand the year (expandExpiry) before submitting, and validate with cardFieldError.
const CardFields = ({
  values,
  errors,
  onChange,
  onBlur,
  disabled = false,
  layout = authLayout,
  ids = defaultIds,
  inputRef,
  errorText,
  cvvHint,
}) => {
  const intl = useIntl();
  const [cvvType, setCvvType] = useState('password');
  const { Field, Row, Input: FieldInput, PasswordWrapper, Error: FieldErrorText, Hint } = layout;

  const cardDigits = digitsOnly(values.cardNumber);
  const cvvLength = expectedCvvLength(cardDigits);
  const showError = error => (errorText ? errorText(error) : errorMessage(intl, error));
  const refFor = key => (inputRef ? inputRef(key) : undefined);
  const errorId = key => (errors[key] ? `${ids[key]}-error` : undefined);
  const showCvvHint = Boolean(Hint && cvvHint && !errors.cvv);

  return (
    <>
      <Field id={ids.cardNumber} label={intl.messages['auth-card-number-label']}>
        <FieldInput
          id={ids.cardNumber}
          ref={refFor('cardNumber')}
          className={errors.cardNumber ? 'is-invalid' : ''}
          value={values.cardNumber}
          onChange={e => {
            const cardNumber = formatCardNumber(e.target.value);
            onChange('cardNumber', cardNumber);
            // Trim a 4th CVV digit if the number now looks like a 3-digit-code network.
            const cvv = values.cvv.slice(0, expectedCvvLength(digitsOnly(cardNumber)));
            if (cvv !== values.cvv) onChange('cvv', cvv);
          }}
          onBlur={() => onBlur('cardNumber')}
          inputMode="numeric"
          autoComplete="cc-number"
          // Grouping spaces count towards maxLength, so allow for them.
          maxLength={maxCardDigits(cardDigits) + 4}
          aria-invalid={Boolean(errors.cardNumber)}
          aria-describedby={errorId('cardNumber')}
          disabled={disabled}
        />
        {errors.cardNumber && (
          <FieldErrorText id={errorId('cardNumber')}>{showError(errors.cardNumber)}</FieldErrorText>
        )}
      </Field>
      <Row>
        <Field
          id={ids.cvv}
          label={intl.messages['auth-cvv-label']}
          tooltip={Hint ? undefined : <InfoTooltip position="top" content={intl.messages['auth-cvv-tooltip']} />}
        >
          <PasswordWrapper>
            <FieldInput
              id={ids.cvv}
              ref={refFor('cvv')}
              className={errors.cvv ? 'is-invalid' : ''}
              value={values.cvv}
              onChange={e => onChange('cvv', digitsOnly(e.target.value).slice(0, cvvLength))}
              onBlur={() => onBlur('cvv')}
              type={cvvType}
              inputMode="numeric"
              autoComplete="cc-csc"
              maxLength={cvvLength}
              aria-invalid={Boolean(errors.cvv)}
              aria-describedby={showCvvHint ? `${ids.cvv}-hint` : errorId('cvv')}
              disabled={disabled}
            />
            <PwdEyeButton
              type={cvvType}
              settype={setCvvType}
              label={intl.messages[cvvType === 'password' ? 'auth-show-cvv' : 'auth-hide-cvv']}
            />
          </PasswordWrapper>
          {errors.cvv && <FieldErrorText id={errorId('cvv')}>{showError(errors.cvv)}</FieldErrorText>}
          {showCvvHint && <Hint id={`${ids.cvv}-hint`}>{cvvHint}</Hint>}
        </Field>
        <Field id={ids.expiry} label={intl.messages['auth-expiry-label']}>
          <FieldInput
            id={ids.expiry}
            ref={refFor('expiry')}
            className={errors.expiry ? 'is-invalid' : ''}
            value={values.expiry}
            onChange={e => onChange('expiry', formatShortExpiry(e.target.value))}
            onBlur={() => onBlur('expiry')}
            placeholder={intl.messages['register-expiry-placeholder']}
            inputMode="numeric"
            autoComplete="cc-exp"
            // Room for a pasted MM/YYYY, which formatShortExpiry shortens to MM/YY.
            maxLength={7}
            aria-invalid={Boolean(errors.expiry)}
            aria-describedby={errorId('expiry')}
            disabled={disabled}
          />
          {errors.expiry && <FieldErrorText id={errorId('expiry')}>{showError(errors.expiry)}</FieldErrorText>}
        </Field>
      </Row>
    </>
  );
};

const errorShape = PropTypes.oneOfType([PropTypes.string, PropTypes.bool, PropTypes.object]);

CardFields.propTypes = {
  values: PropTypes.shape({
    cardNumber: PropTypes.string.isRequired,
    cvv: PropTypes.string.isRequired,
    expiry: PropTypes.string.isRequired,
  }).isRequired,
  errors: PropTypes.shape({
    cardNumber: errorShape,
    cvv: errorShape,
    expiry: errorShape,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  layout: PropTypes.shape({
    Field: PropTypes.elementType.isRequired,
    Row: PropTypes.elementType.isRequired,
    Input: PropTypes.elementType.isRequired,
    PasswordWrapper: PropTypes.elementType.isRequired,
    Error: PropTypes.elementType.isRequired,
    Hint: PropTypes.elementType,
  }),
  ids: PropTypes.shape({
    cardNumber: PropTypes.string.isRequired,
    cvv: PropTypes.string.isRequired,
    expiry: PropTypes.string.isRequired,
  }),
  // name => ref callback, for pages that move focus to the field an error belongs to.
  inputRef: PropTypes.func,
  // error => text, for pages that hold errors as something other than an i18n key.
  errorText: PropTypes.func,
  cvvHint: PropTypes.node,
};

export default CardFields;
